<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import LibraryRemoveConfirmDialog from '@/components/LibraryRemoveConfirmDialog.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import SaveToLibrary from '@/library/SaveToLibrary.vue'
import { useLibraryStore } from '@/library/library.store'
import { useUserStore } from '@/authentication/user.store'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions } from '@/util/access_visibility'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import type { SavingState } from '@/presentlevels/presentlevels_types'

type SavedFolder = { uuid: string, name: string }

const props = defineProps({
  contentUuid: {
    type: String,
    required: true
  },
  lastSavedAt: {
    type: String,
    default: undefined
  },
  isContentSaving: {
    type: Boolean,
    default: false
  },
  isSaveButtonHidden: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  saved: [folder: SavedFolder]
  removed: []
}>()

const analytics = getAnalyticsTracker()
const libraryStore = useLibraryStore()
const { currentFolderUuid } = storeToRefs(libraryStore)
const { authInfo } = storeToRefs(useUserStore())

const savePermissions = computed(() =>
  actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.PRESENTLEVEL, { demo: false }, authInfo.value)
)

const saveStatus = computed<SavingState>(() => {
  if (props.isContentSaving) {
    return PL_SUMMARY_SAVING_STATES.SAVING
  }
  return PL_SUMMARY_SAVING_STATES.NO_CHANGES
})

const removeConfirmGroup = computed(() => `${props.contentUuid}_${currentFolderUuid.value}`)

const saveErrorMessage = ref('')

type ErrorWithMessage = { message: string }

function hasMessage(error: unknown): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && typeof (error as ErrorWithMessage).message === 'string'
}

// The API layer rejects with plain objects carrying a message, not Error instances.
function errorMessageOf(error: unknown) {
  if (hasMessage(error)) {
    return error.message
  }
  return String(error)
}

// The library's current folder is shared with the folder pages, so the panel looks up
// the statement's own folder rather than trusting whatever folder was last opened.
async function showStatementFolder() {
  await libraryStore.fetchFoldersForContentByType(CONTENT_TYPES.PRESENTLEVEL)
  libraryStore.setCurrentFolderByUuid(libraryStore.folderUuidsByContentUuid[props.contentUuid] ?? '')
}

onMounted(showStatementFolder)

async function handleSave(folder: SavedFolder) {
  await showStatementFolder()
  emit('saved', folder)
}

function handleSaveError(error: unknown) {
  saveErrorMessage.value = errorMessageOf(error)
}

function dismissSaveError() {
  saveErrorMessage.value = ''
}

async function handleRemove() {
  analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.LIBRARY_SUMMARY.REMOVED)
  await showStatementFolder()
  emit('removed')
}
</script>

<template>
  <div>
    <template v-if="currentFolderUuid">
      <LibraryContentPanel
        :content-uuid="contentUuid"
        :content-type="CONTENT_TYPES.PRESENTLEVEL"
        :save-status="saveStatus"
        :updated-at="lastSavedAt"
        :is-save-button-hidden="isSaveButtonHidden"
        @remove="handleRemove"
      />
      <LibraryRemoveConfirmDialog :group="removeConfirmGroup" />
    </template>

    <SaveToLibrary
      v-else
      class="py-2.5"
      :content-type="CONTENT_TYPES.PRESENTLEVEL"
      content-type-label="Present Levels Statement"
      :content-uuid="contentUuid"
      size="lg"
      :full-width="true"
      :action-permissions="savePermissions"
      @save="handleSave"
      @saveerror="handleSaveError"
    />

    <Transition
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity motion-reduce:transition-none"
      leave-active-class="transition-opacity motion-reduce:transition-none"
    >
      <GbInlineMessage
        v-if="saveErrorMessage"
        gb-theme="red"
        role="alert"
        class="mt-2 w-full"
        :pt="{ text: { class: 'flex-1' } }"
        :pt-options="{ mergeProps: true }"
      >
        <div class="flex items-start gap-2 text-text-md">
          <span class="flex-1 text-center">{{ saveErrorMessage }}</span>
          <button
            type="button"
            aria-label="Close"
            class="material-icons-round text-text-xl leading-none text-gray-700 hover:text-gray-900"
            @click="dismissSaveError"
          >close</button>
        </div>
      </GbInlineMessage>
    </Transition>
  </div>
</template>
